import { findOverlappingScheduleBlock, resolveScheduleForDate } from "utils/scheduleResolver";
import { isDateKeyBlackout, normalizeClassRelativeFilter, resolveStepForClass } from "utils/automationSequenceModel";
import { getAutoClassChangeOffsetSeconds } from "utils/autoClassChangeConfig";
import { getScheduleRuntimeMode } from "utils/scheduleSettings";

export { getScheduleRuntimeMode };

export const buildDateFromActiveDate = (activeDate) => {
  if (activeDate instanceof Date) {
    return new Date(activeDate.getFullYear(), activeDate.getMonth(), activeDate.getDate());
  }

  if (
    activeDate &&
    Number.isFinite(activeDate.year) &&
    Number.isFinite(activeDate.month) &&
    Number.isFinite(activeDate.day)
  ) {
    return new Date(activeDate.year, activeDate.month, activeDate.day);
  }

  return new Date();
};

/** Wall-clock time on the active calendar day (matches Teacher Hub schedule cards). */
export const buildScheduleNowFromActiveDate = (activeDate) => {
  const resolvedActiveDate = buildDateFromActiveDate(activeDate);
  const wallClock = new Date();
  const scheduleNow = new Date(resolvedActiveDate);
  scheduleNow.setHours(
    wallClock.getHours(),
    wallClock.getMinutes(),
    wallClock.getSeconds(),
    wallClock.getMilliseconds()
  );
  return scheduleNow;
};

export const resolveRuntimeActiveDate = ({
  useActiveDates,
  activeDate,
  now = new Date(),
} = {}) => {
  if (useActiveDates === false) {
    return buildDateFromActiveDate(now);
  }
  return activeDate;
};

const toDateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

const parseTimeToDate = (activeDate, timeString) => {
  if (!(activeDate instanceof Date) || Number.isNaN(activeDate.getTime())) return null;
  if (typeof timeString !== "string" || !timeString.includes(":")) return null;

  const [hours, minutes, seconds] = timeString.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;

  const parsedDate = new Date(activeDate);
  parsedDate.setHours(hours, minutes, Number.isFinite(seconds) ? seconds : 0, 0);
  return parsedDate;
};

const normalizeTriggerMode = (item, defaultMode = "fixed") => {
  if (item?.triggerMode === "classRelative") return "classRelative";
  if (item?.triggerMode === "dateRules") return "dateRules";
  return defaultMode;
};

const resolveRoutineItemForClass = (item = {}, classId = "") =>
  Array.isArray(item?.steps)
    ? {
      ...item,
      steps: item.steps.map((step) => resolveStepForClass(step, classId)),
    }
    : item;

const resolveCalendarClassRelativeTriggers = ({
  item,
  itemIndex,
  classes,
  activeDate,
  userSettings,
  defaultLegacyTriggerMode,
  ignoreAutomationPause = false,
}) => {
  const triggerMode = normalizeTriggerMode(item, defaultLegacyTriggerMode);
  if (triggerMode !== "classRelative" || !item?.active) return [];

  const classIds = Array.isArray(item?.relativeConfig?.classIds)
    ? item.relativeConfig.classIds.filter(Boolean)
    : [];
  if (!classIds.length) return [];

  const resolvedSchedule = resolveScheduleForDate(userSettings || {}, activeDate);
  if (!resolvedSchedule?.template || (!ignoreAutomationPause && resolvedSchedule.isAutomationPaused)) return [];
  const activeDateKey = toDateKey(activeDate);
  const relativeConfig = item?.relativeConfig || {};

  if (isDateKeyBlackout(activeDateKey, relativeConfig.globalBlackoutDates)) return [];

  const scheduledClasses = Array.isArray(resolvedSchedule.template.classes)
    ? resolvedSchedule.template.classes
    : [];
  const reference = relativeConfig?.reference === "classEnd" ? "classEnd" : "classStart";
  const direction = relativeConfig?.direction === "before" ? "before" : "after";
  const offsetSeconds = Math.max(0, Number(relativeConfig?.offsetSeconds) || 0);
  const directionMultiplier = direction === "before" ? -1 : 1;

  return classIds.flatMap((classId) => {
    const classItem = classes?.find((candidateClass) => String(candidateClass?.classId) === String(classId));
    if (!classItem) return [];

    const scheduleBlocks = scheduledClasses.filter(
      (candidateClass) => String(candidateClass?.classId) === String(classId)
    );
    if (!scheduleBlocks.length) return [];

    if (classItem?.active === false) return [];

    const classFilter = normalizeClassRelativeFilter(relativeConfig.classFilters?.[String(classItem.classId)]);
    if (
      classFilter.scheduleMode === "selected" &&
      !classFilter.allowedTemplateIds.includes(String(resolvedSchedule.templateId))
    ) {
      return [];
    }
    if (isDateKeyBlackout(activeDateKey, classFilter.blackoutDates)) return [];

    return scheduleBlocks.flatMap((scheduleClass) => {
      const referenceTimeString =
        reference === "classEnd" ? scheduleClass.endTime : scheduleClass.startTime;
      const referenceTime = parseTimeToDate(activeDate, referenceTimeString);
      if (!referenceTime) return [];

      const startTime = new Date(
        referenceTime.getTime() + directionMultiplier * offsetSeconds * 1000
      );

      return [
        {
          item: resolveRoutineItemForClass(item, classItem.classId),
          itemIndex,
          startTime,
          source: "classRelative",
          triggerMode: "classRelative",
          scheduleSource: "calendar",
          reference: {
            classId: classItem.classId,
            className: classItem.className || scheduleClass.name,
            templateId: resolvedSchedule.templateId,
            templateLabel: resolvedSchedule.label,
            relation: `${direction}:${reference}`,
            offsetSeconds,
            scheduleBlockId: scheduleClass.id || null,
          },
        },
      ];
    });
  });
};

const isDateRuleMatch = (rule, activeDate, activeDateKey) => {
  if (!rule || rule.active === false) return false;

  const ruleType = rule.type === "weekdayRecurring" ? "weekdayRecurring" : "specificDates";
  if (ruleType === "specificDates") {
    const dates = Array.isArray(rule?.dates) ? rule.dates : [];
    return dates.includes(activeDateKey);
  }

  const weekdays = Array.isArray(rule?.weekdays)
    ? rule.weekdays
        .map((weekday) => Number(weekday))
        .filter((weekday) => Number.isInteger(weekday) && weekday >= 0 && weekday <= 6)
    : [];
  return weekdays.includes(activeDate.getDay());
};

const resolveDateRuleClassContext = ({ startTime, resolvedSchedule, classes = [] }) => {
  const scheduledClasses = Array.isArray(resolvedSchedule?.template?.classes)
    ? resolvedSchedule.template.classes
    : [];
  if (!scheduledClasses.length) return null;

  const matchingScheduleBlock = findOverlappingScheduleBlock(scheduledClasses, startTime, {
    includeClassStartWindow: true,
  });
  if (!matchingScheduleBlock?.classId) return null;

  const classItem = classes.find(
    (candidateClass) => String(candidateClass?.classId) === String(matchingScheduleBlock.classId)
  );
  if (!classItem || classItem?.active === false) return null;

  return {
    classItem,
    scheduleBlock: matchingScheduleBlock,
  };
};

const resolveDateRuleTriggers = ({ item, itemIndex, activeDate, classes, resolvedSchedule }) => {
  if (!item?.active || item?.triggerMode !== "dateRules") return [];

  const activeDateKey = toDateKey(activeDate);
  if (!activeDateKey) return [];

  const dateRules = Array.isArray(item?.dateRules) ? item.dateRules : [];
  return dateRules.flatMap((rule, ruleIndex) => {
    if (!isDateRuleMatch(rule, activeDate, activeDateKey)) return [];
    const times = Array.isArray(rule?.times) ? rule.times : [];
    return times.flatMap((timeValue, timeIndex) => {
      const startTime = parseTimeToDate(activeDate, timeValue);
      if (!startTime) return [];
      const classContext = resolveDateRuleClassContext({
        startTime,
        resolvedSchedule,
        classes,
      });
      const classReference = classContext
        ? {
          classId: classContext.classItem.classId,
          className: classContext.classItem.className || classContext.scheduleBlock.name,
          templateId: resolvedSchedule.templateId,
          templateLabel: resolvedSchedule.label,
          scheduleBlockId: classContext.scheduleBlock.id || null,
        }
        : {};

      return [
        {
          item: classContext ? resolveRoutineItemForClass(item, classContext.classItem.classId) : item,
          itemIndex,
          startTime,
          source: "dateRules",
          triggerMode: "dateRules",
          scheduleSource: "calendar",
          reference: {
            ruleId: rule?.id || `rule-${ruleIndex}`,
            time: timeValue,
            ruleIndex,
            timeIndex,
            ...classReference,
          },
        },
      ];
    });
  });
};

const isAutoClassChangeEligible = (classItem = {}) =>
  classItem?.active !== false && classItem?.autoClassChange?.active !== false;

const buildAutoClassChangeTransition = ({
  classItem,
  startTime,
  scheduleSource,
  reference = {},
}) => {
  const offsetSecondsBeforeStart = getAutoClassChangeOffsetSeconds(classItem?.autoClassChange);
  const offsetMinutesBeforeStart = Math.floor(offsetSecondsBeforeStart / 60);
  const transitionTime = new Date(startTime.getTime() - offsetSecondsBeforeStart * 1000);

  return {
    startTime,
    transitionTime,
    offsetMinutesBeforeStart,
    offsetSecondsBeforeStart,
    url: `/${classItem.classId}`,
    classId: classItem.classId,
    showMarquee: classItem.showMarquee,
    showClock: classItem.showClock,
    className: classItem.className,
    scheduleSource,
    scheduleBlockId: reference.scheduleBlockId || null,
    reference,
  };
};

export const getAutoClassChangeTransitionKey = (transition = {}) => {
  const scheduleBlockSegment = transition.scheduleBlockId
    ? `${transition.scheduleBlockId}-`
    : "";
  return `${transition.classId}-${scheduleBlockSegment}${transition.transitionTime.getTime()}`;
};

export const AUTO_CLASS_CHANGE_TRIGGER_WINDOW_MS = 1000;

const isTransitionPathActive = (locationPathname = "", transitionUrl = "") => {
  const normalizedPath = String(locationPathname || "");
  return normalizedPath === transitionUrl || normalizedPath === `${transitionUrl}/`;
};

export const resolveAutoClassChangeNavigation = ({
  transitions = [],
  scheduleNowMs,
  locationPathname = "",
  canceledTransitionKey = null,
  navigatedTransitionKey = null,
  triggerWindowMs = AUTO_CLASS_CHANGE_TRIGGER_WINDOW_MS,
}) => {
  const actionableTransitions = transitions.filter((transition) => {
    const transitionTime = transition?.transitionTime?.getTime();
    if (!Number.isFinite(transitionTime)) return false;
    return transitionTime >= scheduleNowMs - triggerWindowMs;
  });

  if (actionableTransitions.length === 0) {
    return { type: "idle", clearNavigatedKey: true };
  }

  const nextClass = actionableTransitions[0];
  const nextClassTimeDifference = nextClass.transitionTime.getTime() - scheduleNowMs;
  const status = {
    nextClass,
    timeUntilNavigation: nextClassTimeDifference,
  };
  const foremostKey = getAutoClassChangeTransitionKey(nextClass);

  if (
    canceledTransitionKey === foremostKey &&
    nextClassTimeDifference < -triggerWindowMs
  ) {
    return { type: "clear-cancel", ...status };
  }

  for (const transition of actionableTransitions) {
    const transitionKey = getAutoClassChangeTransitionKey(transition);
    if (canceledTransitionKey === transitionKey) continue;
    if (navigatedTransitionKey === transitionKey) continue;

    const timeDifference = transition.transitionTime.getTime() - scheduleNowMs;
    if (isTransitionPathActive(locationPathname, transition.url)) continue;
    if (timeDifference > triggerWindowMs || timeDifference <= -triggerWindowMs) continue;

    return {
      type: "navigate",
      transition,
      transitionKey,
      timeDifference,
      ...status,
    };
  }

  return { type: "wait", ...status };
};

const resolveCalendarAutoClassChangeTransitions = ({ classes, userSettings, activeDate }) => {
  const resolvedSchedule = resolveScheduleForDate(userSettings || {}, activeDate);
  if (!resolvedSchedule?.template || resolvedSchedule.isAutomationPaused) return [];

  const scheduledClasses = Array.isArray(resolvedSchedule.template.classes)
    ? resolvedSchedule.template.classes
    : [];
  const classesById = new Map(
    (Array.isArray(classes) ? classes : []).map((classItem) => [String(classItem.classId), classItem])
  );

  return scheduledClasses.flatMap((scheduleClass) => {
    const classItem = classesById.get(String(scheduleClass?.classId));
    if (!classItem || !isAutoClassChangeEligible(classItem)) return [];

    const startTime = parseTimeToDate(activeDate, scheduleClass.startTime);
    if (!startTime) return [];

    return [
      buildAutoClassChangeTransition({
        classItem,
        startTime,
        scheduleSource: "calendar",
        reference: {
          classId: classItem.classId,
          className: classItem.className || scheduleClass.name,
          templateId: resolvedSchedule.templateId,
          templateLabel: resolvedSchedule.label,
          scheduleBlockId: scheduleClass.id || null,
        },
      }),
    ];
  });
};

export const resolveAutoClassChangeTransitions = ({
  classes = [],
  userSettings = {},
  activeDate,
}) => {
  const normalizedActiveDate = buildDateFromActiveDate(activeDate);
  const transitions = resolveCalendarAutoClassChangeTransitions({
    classes,
    userSettings,
    activeDate: normalizedActiveDate,
  });

  return transitions
    .filter(
      (transition) =>
        transition?.transitionTime instanceof Date &&
        !Number.isNaN(transition.transitionTime.getTime())
    )
    .sort(
      (firstTransition, secondTransition) =>
        firstTransition.transitionTime.getTime() - secondTransition.transitionTime.getTime()
    );
};

export const resolveAutomationStartTimesForCollection = ({
  items = [],
  userSettings = {},
  classes = [],
  activeDate,
  defaultLegacyTriggerMode = "fixed",
  ignoreAutomationPause = false,
}) => {
  const normalizedActiveDate = buildDateFromActiveDate(activeDate);
  const scheduleRuntimeMode = "calendar";
  const resolvedSchedule = resolveScheduleForDate(userSettings || {}, normalizedActiveDate);

  if (resolvedSchedule?.isAutomationPaused && !ignoreAutomationPause) {
    return {
      triggers: [],
      scheduleRuntimeMode,
      activeDate: normalizedActiveDate,
      resolvedSchedule,
      blockedReason: "Automations are paused for this date.",
    };
  }

  const triggers = (Array.isArray(items) ? items : [])
    .map((item, itemIndex) => ({ item, itemIndex }))
    .flatMap(({ item, itemIndex }) => {
      if (!item?.active) return [];

      return [
        ...resolveDateRuleTriggers({
          item,
          itemIndex,
          activeDate: normalizedActiveDate,
          classes,
          resolvedSchedule,
        }),
        ...resolveCalendarClassRelativeTriggers({
          item,
          itemIndex,
          classes,
          activeDate: normalizedActiveDate,
          userSettings,
          defaultLegacyTriggerMode,
          ignoreAutomationPause,
        }),
      ];
    })
    .filter((trigger) => trigger?.startTime instanceof Date && !Number.isNaN(trigger.startTime.getTime()))
    .sort((firstTrigger, secondTrigger) => firstTrigger.startTime.getTime() - secondTrigger.startTime.getTime());

  return {
    triggers,
    scheduleRuntimeMode,
    activeDate: normalizedActiveDate,
    resolvedSchedule,
  };
};
